/**
 * AzureSubscriptionsPanel.jsx (Gate 4)
 * ====================================
 * Azure por conta: cada subscription_guid é uma conta independente.
 * Duas contas compartilham o nome 'Microsoft Azure Sponsorship' —
 * por isso display_name + GUID abreviado são sempre exibidos.
 *
 * REGRAS:
 *  - valores monetários vêm serializados do backend (strings Decimal);
 *  - NENHUM recálculo de crédito/líquido aqui (parseFloat só p/ FORMATAÇÃO);
 *  - totais oficiais = totals da API (reconciliados no backend);
 *  - custo bruto NÃO é chamado de "despesa paga".
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Cloud, RefreshCw, AlertTriangle, Pencil, X, Eye, Clock,
  ArrowUpDown, CheckCircle2, Layers,
} from 'lucide-react';
import {
  getAzureSubscriptionSummary,
  getAzureSubscriptionFreshness,
  getAzureUnmappedDetail,
  updateAzureSubscription,
} from '../../services/finopsService';

const T = {
  primary: '#064e3b', gold: '#b48c2c', text: '#1f2937',
  textLight: '#6b7280', border: '#e5e7eb',
  success: '#059669', warning: '#d97706', danger: '#dc2626',
};

const ENV_LABELS = {
  production: 'Produção', staging: 'Staging', development: 'Dev',
  ai: 'IA', shared: 'Compartilhado', other: 'Outro',
};
const ENV_COLORS = {
  production: '#064e3b', ai: '#7c3aed', staging: '#d97706',
  development: '#2563eb', shared: '#0891b2', other: '#6b7280',
};

// Formatação APENAS para exibição (valores oficiais vêm prontos do backend)
const fmtUsd = (s) => (s == null ? '—'
  : `$${Number(s).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtBrl = (s) => (s == null ? '—'
  : `R$\u00a0${Number(s).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (iso) => (iso ? iso.split('-').reverse().join('/') : '—');
const shortGuid = (g) => (g ? `${g.slice(0, 4)}…${g.slice(-4)}` : '');

const defaultMonth = () => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1); // mês anterior
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// ── Card KPI simples ─────────────────────────────────────────────────────
const Kpi = ({ label, value, sub, color = T.primary }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <p className="text-xs text-gray-500 uppercase font-semibold mb-1">{label}</p>
    <p className="text-xl font-black leading-tight" style={{ color }}>{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

// ── Modal de edição administrativa ───────────────────────────────────────
const EditSubscriptionModal = ({ sub, onClose, onSaved }) => {
  const [form, setForm] = useState({
    display_name: sub.display_name || '',
    description: '',
    environment: sub.environment || 'other',
    status: sub.status || 'active',
    is_credit_eligible: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const resp = await updateAzureSubscription(sub.subscription_guid, form);
      if (resp?.success === false) throw new Error(resp.error || 'Erro ao salvar');
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold" style={{ color: T.primary }}>Editar conta Azure</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X size={18} /></button>
        </div>

        {/* Imutáveis */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4 text-xs text-gray-500 space-y-1">
          <p><span className="font-semibold">Nome original:</span> {sub.subscription_name}</p>
          <p className="font-mono">{sub.subscription_guid}</p>
          <p className="text-gray-400">GUID e nome original não são editáveis (auditados).</p>
        </div>

        <label className="block text-xs font-semibold text-gray-600 mb-1">Nome amigável</label>
        <input
          value={form.display_name}
          onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg mb-3 text-sm"
        />

        <label className="block text-xs font-semibold text-gray-600 mb-1">Ambiente</label>
        <select
          value={form.environment}
          onChange={(e) => setForm({ ...form, environment: e.target.value })}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg mb-3 text-sm"
        >
          {Object.entries(ENV_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>

        <div className="flex gap-4 mb-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.status === 'active'}
              onChange={(e) => setForm({ ...form, status: e.target.checked ? 'active' : 'inactive' })}
            />
            Ativa
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_credit_eligible}
              onChange={(e) => setForm({ ...form, is_credit_eligible: e.target.checked })}
            />
            Elegível para créditos
          </label>
        </div>

        {error && (
          <p className="text-xs mb-3 px-3 py-2 rounded-lg bg-red-50" style={{ color: T.danger }}>{error}</p>
        )}

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-semibold">
            Cancelar
          </button>
          <button
            onClick={save} disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50"
            style={{ backgroundColor: T.primary }}
          >
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modal unmapped ───────────────────────────────────────────────────────
const UnmappedModal = ({ guid, month, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getAzureUnmappedDetail({ reference_month: month, subscription_guid: guid })
      .then((r) => setData(r?.data || r))
      .catch((e) => setError(e?.response?.data?.error || 'Erro ao carregar'));
  }, [guid, month]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold" style={{ color: T.primary }}>
            Itens sem classificação · {month}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X size={18} /></button>
        </div>
        {error && <p className="text-sm" style={{ color: T.danger }}>{error}</p>}
        {!data && !error && <p className="text-sm text-gray-400">Carregando…</p>}
        {data && (
          <>
            <p className="text-sm text-gray-600 mb-3">
              {data.total_items} itens agrupados · total <b>{fmtUsd(data.total_unmapped_cost_usd)}</b>
            </p>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2">Serviço</th>
                  <th>Recurso</th>
                  <th className="text-right">Linhas</th>
                  <th className="text-right">Custo USD</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-2">{it.service_name || '—'}<br />
                      <span className="text-gray-400">{it.service_type}</span></td>
                    <td className="font-mono text-gray-500">{it.resource_name || it.resource_guid || '—'}</td>
                    <td className="text-right">{it.usage_lines}</td>
                    <td className="text-right font-bold">{fmtUsd(it.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

// ── Painel principal ─────────────────────────────────────────────────────
const AzureSubscriptionsPanel = () => {
  const [month, setMonth] = useState(defaultMonth());
  const [envFilter, setEnvFilter] = useState('');
  const [guidFilter, setGuidFilter] = useState('');
  const [summary, setSummary] = useState(null);
  const [freshness, setFreshness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState('gross_cost_usd');
  const [sortDesc, setSortDesc] = useState(true);
  const [editSub, setEditSub] = useState(null);
  const [unmappedSub, setUnmappedSub] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [s, f] = await Promise.all([
        getAzureSubscriptionSummary(month),
        getAzureSubscriptionFreshness(),
      ]);
      setSummary(s?.data || s);
      setFreshness(f?.data || f);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Erro ao carregar dados Azure');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const subs = (summary?.subscriptions || [])
    .filter((s) => !envFilter || s.environment === envFilter)
    .filter((s) => !guidFilter || s.subscription_guid === guidFilter)
    .sort((a, b) => {
      const va = a[sortKey]; const vb = b[sortKey];
      const na = Number(va); const nb = Number(vb);
      const cmp = (!isNaN(na) && !isNaN(nb)) ? na - nb : String(va || '').localeCompare(String(vb || ''));
      return sortDesc ? -cmp : cmp;
    });

  const totals = summary?.totals || {};
  const staleCount = (freshness?.subscriptions || []).filter((s) => s.is_stale && s.status === 'active').length;
  const totalUnmapped = subs.reduce((acc, s) => acc + (s.unmapped_count || 0), 0);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDesc(!sortDesc);
    else { setSortKey(key); setSortDesc(true); }
  };

  const SortTh = ({ label, k, right }) => (
    <th className={`py-2 px-2 cursor-pointer select-none ${right ? 'text-right' : 'text-left'}`}
        onClick={() => toggleSort(k)}>
      <span className="inline-flex items-center gap-1">
        {label}<ArrowUpDown size={11} className="text-gray-400" />
      </span>
    </th>
  );

  return (
    <div className="space-y-5">
      {/* Header + filtros */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Cloud size={18} style={{ color: T.primary }} />
          <h3 className="font-bold text-gray-900">Azure por Conta</h3>
        </div>
        <input
          type="month" value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
        />
        <select
          value={envFilter} onChange={(e) => setEnvFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
        >
          <option value="">Todos ambientes</option>
          {Object.entries(ENV_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select
          value={guidFilter} onChange={(e) => setGuidFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm max-w-[260px]"
        >
          <option value="">Todas as contas</option>
          {(summary?.subscriptions || []).map((s) => (
            <option key={s.subscription_guid} value={s.subscription_guid}>
              {s.display_name} · {shortGuid(s.subscription_guid)}
            </option>
          ))}
        </select>
        <button onClick={load} className="ml-auto p-2 rounded-lg border border-gray-200 hover:bg-gray-50">
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} style={{ color: T.primary }} />
        </button>
      </div>

      {/* Erro sem quebrar a página */}
      {error && (
        <div className="rounded-xl border-2 p-4 flex items-center gap-3"
             style={{ borderColor: T.danger, backgroundColor: '#fef2f2' }}>
          <AlertTriangle size={18} style={{ color: T.danger }} />
          <p className="text-sm font-semibold" style={{ color: T.danger }}>{error}</p>
        </div>
      )}

      {loading && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      )}

      {!loading && !error && summary && (
        <>
          {/* Cards gerais — totais oficiais da API */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            <Kpi label="Custo bruto Azure" value={fmtUsd(totals.gross_cost_usd)}
                 sub={totals.gross_cost_brl ? fmtBrl(totals.gross_cost_brl) : 'PTAX indisponível'} />
            <Kpi label="Créditos aplicados" value={fmtUsd(totals.credit_applied_usd)} color={T.success} />
            <Kpi label="Custo líquido" value={fmtUsd(totals.net_cost_usd)}
                 sub={totals.net_cost_brl ? fmtBrl(totals.net_cost_brl) : null}
                 color={Number(totals.net_cost_usd) > 0 ? T.warning : T.success} />
            <Kpi label="Contas ativas" value={subs.filter((s) => s.status === 'active').length} />
            <Kpi label="Sem classificação" value={totalUnmapped}
                 color={totalUnmapped > 0 ? T.warning : T.success} />
            <Kpi label="Atualizado até" value={fmtDate(freshness?.complete_through_date)}
                 sub={staleCount > 0 ? `${staleCount} conta(s) atrasada(s)` : 'todas as contas'}
                 color={staleCount > 0 ? T.warning : T.primary} />
          </div>

          {summary.fx_is_fallback && (
            <p className="text-xs px-3 py-2 rounded-lg inline-block"
               style={{ backgroundColor: '#fffbeb', color: T.warning }}>
              ⚠ PTAX do fim do mês indisponível — usando última cotação anterior (fallback).
            </p>
          )}

          {/* Cards por assinatura */}
          {subs.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">
              Nenhuma conta com consumo em {month}.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {subs.map((s) => {
                const fresh = (freshness?.subscriptions || [])
                  .find((f) => f.subscription_guid === s.subscription_guid);
                return (
                  <div key={s.subscription_guid}
                       className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col">
                    <div className="flex items-start justify-between mb-1">
                      <div className="min-w-0">
                        <p className="font-bold text-gray-900 truncate">{s.display_name}</p>
                        {/* nome original + GUID sempre visíveis (nomes duplicam!) */}
                        <p className="text-xs text-gray-400 truncate">
                          {s.subscription_name} · <span className="font-mono">{shortGuid(s.subscription_guid)}</span>
                        </p>
                      </div>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white shrink-0"
                            style={{ backgroundColor: ENV_COLORS[s.environment] || T.textLight }}>
                        {ENV_LABELS[s.environment] || s.environment}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-2 my-3">
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <p className="text-sm font-black text-gray-900">{fmtUsd(s.gross_cost_usd)}</p>
                        <p className="text-[10px] text-gray-500">Bruto</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <p className="text-sm font-black" style={{ color: T.success }}>{fmtUsd(s.credit_applied_usd)}</p>
                        <p className="text-[10px] text-gray-500">Crédito</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <p className="text-sm font-black"
                           style={{ color: Number(s.net_cost_usd) > 0 ? T.warning : T.success }}>
                          {fmtUsd(s.net_cost_usd)}
                        </p>
                        <p className="text-[10px] text-gray-500">Líquido</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                      <span className="inline-flex items-center gap-1">
                        <Clock size={11} /> {fmtDate(s.last_usage_date)}
                        {fresh?.is_stale && (
                          <span className="font-bold" style={{ color: T.warning }}> · atrasada</span>
                        )}
                      </span>
                      {s.unmapped_count > 0 ? (
                        <span className="inline-flex items-center gap-1 font-semibold" style={{ color: T.warning }}>
                          <AlertTriangle size={11} /> {s.unmapped_count} sem regra ({fmtUsd(s.unmapped_cost_usd)})
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1" style={{ color: T.success }}>
                          <CheckCircle2 size={11} /> classificado
                        </span>
                      )}
                    </div>

                    <div className="mt-auto flex gap-2">
                      <button
                        onClick={() => setEditSub(s)}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50">
                        <Pencil size={12} /> Editar identificação
                      </button>
                      {s.unmapped_count > 0 && (
                        <button
                          onClick={() => setUnmappedSub(s)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-white"
                          style={{ backgroundColor: T.warning }}>
                          <Eye size={12} /> Ver itens
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Tabela ordenável */}
          {subs.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500 border-b bg-gray-50">
                    <SortTh label="Conta Azure" k="display_name" />
                    <SortTh label="Ambiente" k="environment" />
                    <SortTh label="Bruto USD" k="gross_cost_usd" right />
                    <SortTh label="Crédito USD" k="credit_applied_usd" right />
                    <SortTh label="Líquido USD" k="net_cost_usd" right />
                    <th className="text-right px-2">Líquido BRL</th>
                    <SortTh label="Unmapped" k="unmapped_count" right />
                    <SortTh label="Atualização" k="last_usage_date" right />
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.subscription_guid} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2 px-2">
                        <p className="font-bold text-gray-900">{s.display_name}</p>
                        <p className="text-gray-400 font-mono">{shortGuid(s.subscription_guid)}</p>
                      </td>
                      <td className="px-2">{ENV_LABELS[s.environment] || s.environment}</td>
                      <td className="px-2 text-right font-bold">{fmtUsd(s.gross_cost_usd)}</td>
                      <td className="px-2 text-right" style={{ color: T.success }}>{fmtUsd(s.credit_applied_usd)}</td>
                      <td className="px-2 text-right font-bold">{fmtUsd(s.net_cost_usd)}</td>
                      <td className="px-2 text-right">{s.net_cost_brl != null ? fmtBrl(s.net_cost_brl) : '—'}</td>
                      <td className="px-2 text-right">
                        {s.unmapped_count > 0
                          ? <span className="font-bold" style={{ color: T.warning }}>{s.unmapped_count}</span>
                          : '0'}
                      </td>
                      <td className="px-2 text-right">{fmtDate(s.last_usage_date)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-black bg-gray-50">
                    <td className="py-2 px-2" colSpan={2}>
                      <span className="inline-flex items-center gap-1">
                        <Layers size={12} /> Total (oficial da API)
                      </span>
                    </td>
                    <td className="px-2 text-right">{fmtUsd(totals.gross_cost_usd)}</td>
                    <td className="px-2 text-right" style={{ color: T.success }}>{fmtUsd(totals.credit_applied_usd)}</td>
                    <td className="px-2 text-right">{fmtUsd(totals.net_cost_usd)}</td>
                    <td className="px-2 text-right">{totals.net_cost_brl != null ? fmtBrl(totals.net_cost_brl) : '—'}</td>
                    <td className="px-2 text-right">{totalUnmapped}</td>
                    <td className="px-2" />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}

      {editSub && (
        <EditSubscriptionModal
          sub={editSub}
          onClose={() => setEditSub(null)}
          onSaved={() => { setEditSub(null); load(); }}
        />
      )}
      {unmappedSub && (
        <UnmappedModal
          guid={unmappedSub.subscription_guid}
          month={month}
          onClose={() => setUnmappedSub(null)}
        />
      )}
    </div>
  );
};

export default AzureSubscriptionsPanel;
